import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "./Navbar.css";
import {
  MenuIcon,
  CalendarIcon,
  DashboardIcon,
  UsersIcon,
  RegisterIcon,
  StudentsIcon,
  EditIcon,
  DownloadIcon,
  ViewIcon,
  FeeIcon,
  PromoteIcon,
  DeleteIcon,
  LogoutIcon,
  UserIcon,
  GradeIcon,
  ReportIcon,
  CampusIcon,
} from "../icons/Icons";

/** Maps the current route path to a human-readable header title. */
const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/students/register": "Register Student",
  "/students/manage": "Record Management",
  "/students/bulk-delete": "Bulk Delete",
  "/admin/school-reports": "School Reports",
  "/admin/student-reports": "Student Reports",
  "/admin/passed-out": "Passed Out Students",
  "/controller/school-reports": "School Reports",
  "/controller/student-reports": "Student Reports",
  "/controller/passed-out": "Passed Out Students",
  "/controller/system-reports": "System Reports",
  "/controller/campus-analytics": "Campus Analytics",
  "/fees": "Fee Management",
  "/superadmin/csv-export": "Download CSV",
  "/superadmin/campuses": "Campus Management",
  "/superadmin/admins": "Admin Management",
  "/superadmin/reports": "System Reports",
  "/viewer/campus-analytics": "Campus Analytics",
  // Teacher pages
  "/teacher/timetable": "My Timetable",
  "/teacher/students": "My Students",
  "/teacher/grades": "Manage Grades",
  "/teacher/reports": "Reports",
  "/teacher/profile": "My Profile",
  "/teacher/campus": "Campus Info",
  // Parent pages
  "/parent/overview": "Parent Dashboard",
  "/parent/select-child": "My Children",
  "/parent/analytics": "Analytics",
  "/parent/calendar": "Yearly Calendar",
  "/parent/timetable": "Timetable",
  "/parent/fees": "Fees",
  "/parent/notifications": "Notifications",
  "/parent/profile": "Profile",
};

/** Icons for each page */
const PAGE_ICONS = {
  "/dashboard": DashboardIcon,
  "/superadmin/campuses": DashboardIcon,
  "/superadmin/admins": UsersIcon,
  "/admin/school-reports": ViewIcon,
  "/admin/student-reports": StudentsIcon,
  "/admin/passed-out": PromoteIcon,
  "/controller/school-reports": ViewIcon,
  "/controller/student-reports": StudentsIcon,
  "/controller/passed-out": PromoteIcon,
  "/controller/system-reports": ViewIcon,
  "/controller/campus-analytics": DashboardIcon,
  "/students/register": RegisterIcon,
  "/students/manage": EditIcon,
  "/students/bulk-delete": DeleteIcon,
  "/superadmin/csv-export": DownloadIcon,
  "/superadmin/reports": ViewIcon,
  "/viewer/campus-analytics": ViewIcon,
  "/fees": FeeIcon,
  // Teacher icons
  "/teacher/timetable": CalendarIcon,
  "/teacher/students": StudentsIcon,
  "/teacher/grades": GradeIcon,
  "/teacher/reports": ReportIcon,
  "/teacher/profile": UserIcon,
  "/teacher/campus": CampusIcon,
};

const ROLE_LABELS = {
  superadmin: "Superadmin User",
  principal: "Campus Principal",
  administrator: "Campus Administrator",
  accountant: "Accountant",
  admin: "Admin User",
  viewer: "Viewer",
  teacher: "Teacher",
  parent: "Parent",
};

const titleFromPath = (pathname) => {
  const last = pathname.split("/").filter(Boolean).pop() || "";
  return last.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

function Navbar({
  isEditingStudent,
  sidebarOpen,
  setSidebarOpen,
  loginRole,
  onLogout,
}) {
  const { pathname } = useLocation();
  const role = (loginRole || "").toLowerCase();

  const today = new Date();
  // Get day, month, date for better display
  const dayName = today.toLocaleDateString("en-US", { weekday: "short" });
  const monthName = today.toLocaleDateString("en-US", { month: "short" });
  const dayNumber = today.getDate();
  const year = today.getFullYear();

  const isFeeDetails = pathname.startsWith("/student-fee-details/");
  // Get teacher/parent display name from localStorage if available
  const teacherName = localStorage.getItem("als_teacher_name") || "";
  const parentName = localStorage.getItem("als_parent_name") || "";
  const portalUserName =
    role === "teacher"
      ? teacherName
      : role === "parent"
        ? parentName
        : teacherName;

  // Resolve page title — campus fee pages (/fees/campus/:id) should just
  // show "Fee Management", not the raw campus-id slug.
  const pageTitle =
    pathname === "/dashboard" && role === "viewer"
      ? "System Reports"
      : (pathname === "/dashboard" || pathname === "/teacher/overview") &&
          role === "teacher"
        ? "Teacher Dashboard"
        : (pathname === "/dashboard" || pathname === "/parent/overview") &&
            role === "parent"
          ? "Parent Dashboard"
        : pathname === "/students/register" && isEditingStudent
          ? "Edit Student"
          : isFeeDetails
            ? "Student Fee Details"
            : pathname.startsWith("/fees/campus/")
              ? "Fee Management"
              : PAGE_TITLES[pathname] || titleFromPath(pathname) || "Dashboard";

  const PageIcon = isFeeDetails
    ? FeeIcon
    : PAGE_ICONS[pathname] || DashboardIcon;

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get user initials for avatar
  const getUserInitials = () => {
    if ((role === "teacher" || role === "parent") && portalUserName) {
      return portalUserName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return "AD";
  };

  const getRoleLabel = () => {
    if ((role === "teacher" || role === "parent") && portalUserName) {
      return portalUserName;
    }
    return ROLE_LABELS[role] || "Campus Admin";
  };

  return (
    <header className="topbar">
      {/* LEFT SECTION */}
      <div className="topbar-left">
        <button
          className="menu-toggle"
          onClick={() => setSidebarOpen((prev) => !prev)}
          aria-label="Toggle sidebar"
        >
          <MenuIcon />
        </button>

        <div className="page-header">
          <span className="page-icon-badge">
            <PageIcon />
          </span>
          <div className="page-title-group">
            <span className="page-context">
              {role === "teacher"
                ? "Teacher Portal"
                : role === "parent"
                  ? "Parent Portal"
                  : "School Management"}
            </span>
            <h1 className="page-title">{pageTitle}</h1>
          </div>
        </div>
      </div>

      {/* CENTER SECTION - Now empty, can be used for other purposes */}
      <div className="topbar-center">
        {/* You can add search or other components here if needed */}
      </div>

      {/* RIGHT SECTION - Date and User combined */}
      <div className="topbar-right">
        {/* Date Card */}
        {/*<div className="date-card">
          <div className="date-icon-wrapper">
            <CalendarIcon />
          </div>
          <div className="date-content">
            <div className="date-day">{dayName}</div>
            <div className="date-main">
              <span className="date-number">{dayNumber}</span>
              <span className="date-month">{monthName}</span>
              <span className="date-year">{year}</span>
            </div>
          </div>
        </div>*/}

        <div className="divider-vertical"></div>

        {/* User Menu */}
        <div className="navbar-user-wrapper" ref={userMenuRef}>
          <button
            className="navbar-user"
            onClick={() => setShowUserMenu((prev) => !prev)}
            aria-label="User menu"
          >
            <div className="user-avatar">{getUserInitials()}</div>

            <div className="user-info">
              <span className="user-name">{getRoleLabel()}</span>
              <span className={`role-badge ${role}`}>
                {role.toUpperCase()}
              </span>
            </div>
          </button>

          {showUserMenu && (
            <div className="navbar-dropdown">
              {role === "teacher" && (
                <>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      window.location.href = "/teacher/profile";
                    }}
                  >
                    <UserIcon />
                    <span>My Profile</span>
                  </button>
                  <div className="dropdown-divider" />
                </>
              )}
              <button
                type="button"
                className="dropdown-item logout"
                onClick={() => {
                  setShowUserMenu(false);
                  onLogout();
                }}
              >
                <LogoutIcon />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Navbar;
